/**
 * Persistente Statusleiste — wird ganz oben in der App angezeigt,
 * solange der User eingestempelt ist (= eine offene Schicht mit
 * unausgefüllter Endzeit existiert).
 *
 * Klickt man auf "Ausstempeln", wird die aktuelle Uhrzeit als endTime
 * gesetzt, der Status auf "done", und die Schicht ist abgeschlossen.
 */

import { useEffect, useState } from 'react';
import { useActiveOpenShift, useUpdateShift } from '../data/shifts';
import { useLocationsMap } from '../data/locations';
import { hapticError, hapticSuccess } from '../lib/haptics';
import {
  dismissClockedInNotification,
  showClockedInNotification,
} from '../lib/clockNotification';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function nowHHMM(): string {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Formatiert eine Dauer als „1 h 23 min" oder „47 min". */
function formatElapsed(startHHMM: string): string {
  const [sh, sm] = startHHMM.split(':').map(Number);
  const now = new Date();
  const startMin = (sh ?? 0) * 60 + (sm ?? 0);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  let diff = nowMin - startMin;
  if (diff < 0) diff += 24 * 60;
  if (diff < 60) return `${diff} min`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

export function ClockStatusBar() {
  const shift = useActiveOpenShift();
  const locations = useLocationsMap();
  const update = useUpdateShift();

  // Re-render alle 30 Sek, damit der "Eingestempelt seit"-Counter aktuell bleibt
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!shift) return;
    const t = setInterval(() => setTick((n) => n + 1), 30 * 1000);
    return () => clearInterval(t);
  }, [shift]);

  // Beim Erkennen einer aktiven Schicht (auch nach App-Restart):
  // Persistente Notification (re-)anzeigen. Beim Verschwinden der Schicht:
  // Notification dismissen.
  useEffect(() => {
    if (!shift) {
      void dismissClockedInNotification();
      return;
    }
    const loc = locations.get(shift.locationId);
    void showClockedInNotification(
      { id: shift.id, startTime: shift.startTime },
      loc?.name ?? 'Standort',
    );
  }, [shift, locations]);

  if (!shift) return null;

  const location = locations.get(shift.locationId);

  async function onClockOut() {
    if (!shift) return;
    if (!confirm('Jetzt ausstempeln? Die aktuelle Uhrzeit wird als Endzeit gesetzt.')) return;
    try {
      await update.mutateAsync({
        id: shift.id,
        input: {
          endTime: nowHHMM(),
          status: 'done',
        },
      });
      void dismissClockedInNotification();
      hapticSuccess();
    } catch (err) {
      hapticError();
      alert(err instanceof Error ? err.message : 'Ausstempeln fehlgeschlagen');
    }
  }

  return (
    <div
      className="bg-emerald-600/90 text-white px-3 sm:px-6 py-2 border-b border-emerald-700"
      role="status"
    >
      <div className="mx-auto w-full max-w-7xl flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse shrink-0" aria-hidden />
          <p className="text-xs sm:text-sm truncate">
            <span className="font-medium">Eingestempelt</span>{' '}
            bei <span className="font-medium">{location?.name ?? 'Standort?'}</span>{' '}
            seit {shift.startTime} ({formatElapsed(shift.startTime)})
          </p>
        </div>
        <button
          onClick={() => void onClockOut()}
          disabled={update.isPending}
          className="text-xs sm:text-sm px-3 py-1.5 rounded bg-white text-emerald-700 font-medium hover:bg-emerald-50 disabled:opacity-50 !min-h-0 shrink-0"
        >
          {update.isPending ? '…' : 'Ausstempeln'}
        </button>
      </div>
    </div>
  );
}
